import React from 'react';
import { Document, Page, View, Text, StyleSheet, Image, Font } from '@react-pdf/renderer';
import Category from './Category';
import ProductGrid from './GridFour';
import FooterFour from './FooterFour';
import CardFour from './CardFour';
import HeaderThemeOne from './HeaderThemeOne';
import fontBold from './fonts/NotoSansTamil-Bold.ttf';
import fontRegular from './fonts/NotoSansTamil-Regular.ttf';

// Register fonts
Font.register({
  family: 'NotoSansTamil',
  fonts: [
    { src: fontRegular, fontWeight: 'normal' },
    { src: fontBold, fontWeight: 'bold' },
  ],
});

// Dynamic styles based on theme
const createStyles = (theme = {}, hasBgImage = false) => {
  const isTheme2 = Boolean(theme?.enableTheme2);
  const pageBg = theme.pageBackground || theme.extractedBgColor || theme.tableBg || '#ffffff';
  return StyleSheet.create({
    page: {
      flexDirection: 'column',
      padding: 0,
      position: 'relative',
      backgroundColor: pageBg,
    },
    section: {
      flexGrow: 1,
      border: `1px solid ${theme.bordercolor || 'black'}`,
      margin: theme.pageMargin !== undefined ? theme.pageMargin : 10,
      backgroundColor: isTheme2
        ? pageBg
        : (theme.tableBg ? theme.tableBg : (hasBgImage ? 'transparent' : '#ffffff')),
      position: 'relative',
      overflow: 'hidden',
    },
    backgroundImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    footer: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
    },
    fullPageImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    groupHeader: {
      fontSize: theme.headerFontSize || 12,
      fontWeight: 'bold',
      textAlign: 'center',
      color: theme.categoryText || 'black',
      backgroundColor: theme.categoryBg || 'yellow',
      padding: 5,
      marginBottom: 2,
    },
  });
};

// Helper: Flatten into stream with category and group markers
const buildProductStream = (categories, products, groupedProducts) => {
  if (!categories || !products) return [];
  const stream = [];

  // Add categories and their products
  categories.forEach((cat) => {
    const catProducts = products.filter((p) => p.category_name === cat.category_name);
    if (catProducts.length > 0) {
      stream.push({
        __type: 'category',
        name: cat.category_name,
        discount: cat.discount,
        discountToggle: cat.discountToggle, // Include toggle state
      });
      catProducts.forEach((p) => stream.push({ __type: 'product', ...p, discount: cat.discount, discountToggle: cat.discountToggle }));
    }
  });

  // Add grouped products
  if (groupedProducts && groupedProducts.length > 0) {
    groupedProducts.forEach((group) => {
      stream.push({
        __type: 'group',
        name: group.group_name,
        price: group.group_price,
      });
      group.items.forEach((item) => {
        stream.push({
          __type: 'group_item',
          s_no: item.s_no,
          product_name: item.product_name,
          per_unit: item.per_unit,
        });
      });
    });
  }

  return stream;
};


// Helper: Split stream into pages dynamically considering item weights (category/group headers vs products) and A4 page height limits to prevent react-pdf overflow
const chunkProductStream = (stream, firstPageLimit, normalPageLimit, hideFooter = false) => {
  if (!stream || stream.length === 0) return [];
  const chunks = [];
  let currentChunk = [];
  let currentWeight = 0;
  let isFirstPage = true;

  // Physical A4 printable height caps: Page 1 with header <= 46 units (without footer); Normal pages <= 54 units (without footer)
  const maxFirstCap = hideFooter ? 46 : 34;
  const maxNormalCap = hideFooter ? 54 : 42;

  const maxSafePageWeight = (isFirst) => (isFirst ? Math.min(maxFirstCap, firstPageLimit) : Math.min(maxNormalCap, normalPageLimit));

  for (let i = 0; i < stream.length; i++) {
    const item = stream[i];
    // Category or Group headers take 1.2 weight units, product rows take 1.0
    const itemWeight = (item.__type === 'category' || item.__type === 'group') ? 1.2 : 1.0;

    const limit = maxSafePageWeight(isFirstPage);

    if (
      currentChunk.length > 0 &&
      (currentWeight + itemWeight > limit || currentChunk.length >= (isFirstPage ? firstPageLimit : normalPageLimit))
    ) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentWeight = 0;
      isFirstPage = false;
    }

    currentChunk.push(item);
    currentWeight += itemWeight;
  }

  if (currentChunk.length > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
};

const BillFour = ({ bgImageSrc, products, categoryRows, company, theme = {}, groupedProducts, customPageConfig }) => {
  const isTheme2 = Boolean(theme?.enableTheme2);
  const styles = createStyles(theme, Boolean(bgImageSrc));
  const showTheme1Header = Boolean(theme?.enableTheme1Header && !isTheme2);
  const hideFooter = Boolean(theme?.hideFooter || showTheme1Header || isTheme2);
  const renderBgOnPages = Boolean(bgImageSrc) && !isTheme2 && theme?.showBgOnAllPages !== false;

  const defaultProductsPerPage = hideFooter ? 54 : 44;
  const productsPerPage = theme.productsPerPage || defaultProductsPerPage;
  const defaultFirstPageLimit = showTheme1Header ? (hideFooter ? 42 : 32) : productsPerPage;
  const firstPageProducts = theme.firstPageProducts !== undefined ? theme.firstPageProducts : defaultFirstPageLimit;

  // Validate inputs
  const safeCategories = categoryRows || [];
  const safeProducts = products || [];
  // Build flattened list with categories, products, and grouped products
  const stream = buildProductStream(safeCategories, safeProducts, groupedProducts);
  const pages = chunkProductStream(stream, firstPageProducts, productsPerPage, hideFooter);

  let productCounter = 0; // Continuous counter for regular products
  let groupItemCounter = 0;

  const totalCatalogPages = (pages || []).length;
  const hasCustomImage = Boolean(customPageConfig?.imageSrc);
  const mode = customPageConfig?.mode || 'replace_last';
  const targetPage = customPageConfig?.pageNum || totalCatalogPages;

  return (
    <Document>
      {/* 🖼️ Full Cover Page (Page 1) when Background Image is uploaded */}
      {Boolean(bgImageSrc) && (
        <Page
          key="bg-cover-page"
          size="A4"
          orientation={theme.pageOrientation || 'portrait'}
          style={styles.page}
          cache={true}
        >
          <Image src={bgImageSrc} style={styles.fullPageImage} />
        </Page>
      )}

      {(pages || []).map((pageItems, pageIndex) => {
        const pageNum = pageIndex + 1;
        const isLastPage = pageNum === totalCatalogPages;

        // Check if this page should be replaced by the custom edited page image
        const isReplacedPage =
          hasCustomImage &&
          ((mode === 'replace_last' && isLastPage && totalCatalogPages > 1) ||
           (mode === 'replace_num' && pageNum === targetPage && totalCatalogPages > 1));

        if (isReplacedPage) {
          return (
            <Page
              key={`custom-page-replaced-${pageIndex}`}
              size="A4"
              orientation={theme.pageOrientation || 'portrait'}
              style={styles.page}
              cache={true}
            >
              <Image src={customPageConfig.imageSrc} style={styles.fullPageImage} />
            </Page>
          );
        }

        const hasRegularProducts = (pageItems || []).some((item) => item.__type === 'product');
        const itemsOnThisPage = (pageItems || []).length;
        const hasPageHeader = Boolean(showTheme1Header && pageIndex === 0);

        return (
          <Page
            key={`page-${pageIndex}`}
            size="A4"
            orientation={theme.pageOrientation || 'portrait'}
            style={styles.page}
            cache={true}
          >
            {renderBgOnPages && <Image src={bgImageSrc} style={styles.backgroundImage} fixed />}
            <View style={styles.section}>

              {/* ✅ Show Theme 1 Predefined Header ONLY on First Catalog Page (Page 1) */}
              {hasPageHeader && (
                <HeaderThemeOne company={company} theme={theme} />
              )}

              <View style={{ flexGrow: 1, flexDirection: 'column' }}>
                {/* ✅ Show 7-col header ONLY if this page has regular products */}
                {hasRegularProducts && (
                  <CardFour
                    sno="S No"
                    product="Product Name"
                    netrate="Net Rate"
                    per="Per"
                    discountrate="Discount Rate"
                    Rs="Rs"
                    Ps="Ps"
                    isHeader
                    theme={theme}
                    itemsOnThisPage={itemsOnThisPage}
                    hasPageHeader={hasPageHeader}
                  />
                )}

                {(pageItems || []).map((item, idx) => {
                  if (item.__type === 'category') {
                    return (
                      <Category
                        key={`cat-${pageIndex}-${idx}`}
                        title={item.name}
                        discount={item.discount}
                        pageIndex={pageIndex}
                        discountToggle={item.discountToggle}
                        theme={theme}
                      />
                    );
                  } else if (item.__type === 'group') {
                    groupItemCounter = 0; // Reset counter for new group
                    return (
                      <React.Fragment key={`group-${pageIndex}-${idx}`}>
                        <Text style={styles.groupHeader}>
                          {item.name} {item.price} Rs
                        </Text>
                        {/* 👇 Add 3-col header for group items */}
                        <CardFour
                          sno="S No"
                          product="Product"
                          per="Per"
                          isHeader
                          isGroupItem={true}
                          theme={theme}
                          itemsOnThisPage={itemsOnThisPage}
                          hasPageHeader={hasPageHeader}
                        />
                      </React.Fragment>
                    );
                  } else if (item.__type === 'group_item') {
                    groupItemCounter++;
                    return (
                      <CardFour
                        key={`group-item-${pageIndex}-${idx}`}
                        sno={item.s_no}
                        product={item.product_name}
                        per={item.per_unit}
                        isGroupItem={true}
                        theme={theme}
                        itemsOnThisPage={itemsOnThisPage}
                        hasPageHeader={hasPageHeader}
                      />
                    );
                  } else {
                    productCounter++;
                    return (
                      <ProductGrid
                        key={`prod-${pageIndex}-${idx}`}
                        products={[item]}
                        categoryDiscount={item.discount}
                        productCounter={productCounter}
                        theme={theme}
                        itemsOnThisPage={itemsOnThisPage}
                        hasPageHeader={hasPageHeader}
                      />
                    );
                  }
                })}
              </View>

              {!hideFooter && (
                <View style={styles.footer}>
                  <FooterFour
                    company={company}
                    pageIndex={pageIndex + 1}
                    totalPages={pages.length}
                    theme={theme}
                  />
                </View>
              )}
            </View>
          </Page>
        );
      })}

      {/* Append custom page image as an extra page at the end of the PDF */}
      {hasCustomImage && (mode === 'append_end' || totalCatalogPages <= 1) && (
        <Page
          key="custom-page-appended"
          size="A4"
          orientation={theme.pageOrientation || 'portrait'}
          style={styles.page}
        >
          <Image src={customPageConfig.imageSrc} style={styles.fullPageImage} />
        </Page>
      )}
    </Document>
  );
};

export default BillFour;